import React, { useState, useEffect, useRef } from 'react';
import { Check, Star, Zap, CheckCircle2, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { toast } from 'sonner';
import Reveal from '../components/Reveal';
import PackageComparison from '../components/PackageComparison';
import { useSearchParams, Link } from 'react-router-dom';
import RefundPolicy from '../components/RefundPolicy';
import OrderSummary from '../components/OrderSummary';
import SongDetailsFields from '../components/SongDetailsFields';
import MakeItPersonal from '../components/MakeItPersonal';
import { DEPOSIT_TERMS, REFUND_POLICY_LINK, SONG_ADDON, EMPTY_SONG_DETAILS, computeOrder } from '../utils/orderPricing';

const API = process.env.REACT_APP_BACKEND_URL || '';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const PricingPage = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    slideshowType: '',
    message: '',
    rushDelivery: false,
    extraRevisions: 0,
    personalisedSong: false,
    songDetails: EMPTY_SONG_DETAILS
  });
  const [songErrors, setSongErrors] = useState(false);
  const [searchParams] = useSearchParams();
  const packagesRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploadLater, setUploadLater] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({ name: false, email: false, phone: false, slideshowType: false });
  const [pricingBanner, setPricingBanner] = useState(DEPOSIT_TERMS);
  const orderFormRef = useRef(null);

  // Lightweight inline validation
  const validators = {
    name: (v) => v.trim().length >= 2,
    email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    phone: (v) => v.trim() === '' || /^[+\d][\d\s().-]{8,}$/.test(v.trim()),
    slideshowType: (v) => v !== '',
  };
  const isValid = (field) => validators[field] ? validators[field](formData[field] || '') : true;
  const showError = (field) => touched[field] && !isValid(field);
  const showSuccess = (field) => (formData[field] || '').trim() !== '' && isValid(field);

  const fieldClass = (field) =>
    `w-full px-4 py-3 bg-white/10 text-white placeholder-gray-400 border rounded-lg focus:outline-none transition-colors ${
      showError(field)
        ? 'border-red-500 focus:border-red-400'
        : showSuccess(field)
        ? 'border-green-500/70 focus:border-green-400'
        : 'border-white/20 focus:border-amber-500'
    }`;

  // Auto-scroll to order form when a package is selected
  const handleSelectPackage = (pkg) => {
    const wasAlreadySelected = selectedPackage?.id === pkg.id;
    setSelectedPackage(pkg);
    // Use requestAnimationFrame to wait for the form to render before scrolling
    if (!wasAlreadySelected) {
      setTimeout(() => {
        orderFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  };

  // Pre-tick the song when arriving from an "Add to My Order" song link
  useEffect(() => {
    if (searchParams.get('song') === '1') {
      setFormData((f) => ({ ...f, personalisedSong: true }));
    }
  }, [searchParams]);

  const handleAddSong = () => {
    setFormData((f) => ({ ...f, personalisedSong: true }));
    if (selectedPackage) {
      orderFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      toast.success('Personalised song added - now choose your package');
      packagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fetch pricing from database on component mount
  useEffect(() => {
    fetchPricing();
    axios
      .get(`${API}/api/site-settings`)
      .then((res) => {
        if (res.data?.settings?.pricing_banner) {
          setPricingBanner(res.data.settings.pricing_banner);
        }
      })
      .catch(() => {});
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/pricing`);
      if (response?.data?.success && Array.isArray(response.data.packages) && response.data.packages.length > 0) {
        // Use database packages directly with proper structure
        const dbPackages = response.data.packages.map((dbPkg) => ({
          id: dbPkg.id || '',
          name: dbPkg.name || '',
          price: dbPkg.price || 0,
          description: dbPkg.description || '',
          features: Array.isArray(dbPkg.features) ? dbPkg.features : [],
          popular: dbPkg.id === 'standard' // Standard is always most popular
        }));
        
        // Sort packages: Basic → Standard → Premium (by price ascending)
        const sortedPackages = dbPackages.sort((a, b) => a.price - b.price);
        
        setPackages(sortedPackages);
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      // Set fallback pricing only if API fails
      setPackages([
        {
          id: 'basic',
          name: 'Essential Collection',
          price: 100,
          description: 'Ideal for smaller celebrations and meaningful moments',
          features: ['Up to 20 photos', 'Video clips included', '1 music track', 'Cinematic editing', '1 free revision', '1-3 days estimated production', 'Digital + physical USB delivery included'],
          popular: false
        },
        {
          id: 'standard',
          name: 'Signature Collection',
          price: 150,
          description: 'Perfect for most occasions - our most popular choice',
          features: ['Up to 40 photos', 'Video clips included', '2 music tracks', 'Custom text overlays', 'Premium transitions', 'Enhanced cinematic editing', '2 free revisions', '2-4 days estimated production', 'Digital + physical USB delivery included'],
          popular: true
        },
        {
          id: 'premium',
          name: 'Cinematic Collection',
          price: 200,
          description: 'The complete package for milestone events and weddings',
          features: ['Up to 60 photos', 'More extensive video integration', '3 music tracks', 'Advanced effects', 'Cinematic editing', '3 free revisions', '3-5 days estimated production', '15-second highlight reel', 'Digital + physical USB delivery included'],
          popular: false
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fixed per-package deposit; add-ons go on the remaining balance
  const summary = computeOrder(selectedPackage, formData);
  const calculateTotal = () => summary.total.toFixed(2);
  const calculateDeposit = () => summary.deposit.toFixed(2);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    toast.success(`${selectedFiles.length} file(s) selected`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }

    // Mark every field touched so validation messages surface
    setTouched({ name: true, email: true, phone: true, slideshowType: true });

    const invalid = ['name', 'email', 'phone', 'slideshowType'].filter((f) => !validators[f](formData[f] || ''));
    if (invalid.length) {
      toast.error('Please fix the highlighted fields before continuing');
      return;
    }

    const song = formData.songDetails;
    if (formData.personalisedSong && (!song.names.trim() || !song.occasion.trim())) {
      setSongErrors(true);
      toast.error('Please add the names and occasion for your personalised song');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create Stripe checkout session
      const checkoutResponse = await axios.post(`${API}/api/create-checkout-session`, {
        name: formData.name,
        email: formData.email,
        photoCount: 0,
        estimatedPrice: parseFloat(calculateTotal()),
        depositAmount: parseFloat(calculateDeposit()),
        slideshowType: formData.slideshowType,
        packageName: selectedPackage.name,
        packageId: selectedPackage.id,
        phone: formData.phone,
        message: formData.message,
        rushDelivery: formData.rushDelivery,
        extraRevisions: formData.extraRevisions,
        personalisedSong: formData.personalisedSong,
        songDetails: formData.personalisedSong ? formData.songDetails : null
      });

      if (checkoutResponse.data.success) {
        const orderId = checkoutResponse.data.orderId;
        
        // Upload files if any were selected
        if (files.length > 0 && !uploadLater) {
          console.log(`📤 Uploading ${files.length} files for order ${orderId}`);
          toast.info(`Uploading ${files.length} file(s)...`, { duration: 2000 });
          
          const formDataFiles = new FormData();
          files.forEach(file => {
            formDataFiles.append('files', file);
          });
          
          try {
            const uploadResponse = await axios.post(`${API}/api/upload-files/${orderId}`, formDataFiles, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000
            });
            console.log('✅ Files uploaded successfully:', uploadResponse.data);
            toast.success(`${files.length} file(s) uploaded successfully!`);
          } catch (uploadError) {
            console.error('❌ File upload error:', uploadError);
            toast.error(`File upload failed! You can upload files later using order ID: ${orderId}`, { duration: 8000 });
            setIsSubmitting(false);
            return;
          }
        }
        
        // Send enquiry details
        try {
          await axios.post(`${API}/api/enquiries`, {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            slideshowType: formData.slideshowType,
            photoCount: 0,
            estimatedPrice: parseFloat(calculateTotal()),
            musicPreference: '',
            message: formData.message,
            uploadLater: uploadLater,
            files: files.map(f => f.name),
            orderId: orderId,
            packageName: selectedPackage.name,
            rushDelivery: formData.rushDelivery,
            extraRevisions: formData.extraRevisions
          });
        } catch (emailError) {
          console.error('Email error:', emailError);
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('🔄 Redirecting to Stripe checkout...');
        window.location.href = checkoutResponse.data.checkoutUrl;
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 md:pt-40 pb-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <Reveal className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 pb-2 bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
            Choose Your Package
          </h1>
          <p className="text-gray-400 text-lg">
            Photo, video, music & effects - woven into cinematic presentations for every occasion.
          </p>
        </Reveal>

        {/* Packages Grid */}
        <div ref={packagesRef} className="grid md:grid-cols-3 gap-8 mb-16 scroll-mt-44">
          {loading ? (
            // Loading skeleton
            [1, 2, 3].map((n) => (
              <div key={n} className="bg-white/5 rounded-2xl p-8 border border-white/10 animate-pulse">
                <div className="h-8 bg-white/10 rounded mb-4"></div>
                <div className="h-12 bg-white/10 rounded mb-4"></div>
                <div className="h-6 bg-white/10 rounded mb-6"></div>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-4 bg-white/10 rounded"></div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            packages.map((pkg, index) => (
            <Reveal
              key={pkg.id}
              delay={Math.min(index + 1, 3)}
              onClick={() => handleSelectPackage(pkg)}
              className={`relative cursor-pointer rounded-2xl p-8 transition-all duration-300 ${
                selectedPackage?.id === pkg.id
                  ? 'bg-gradient-to-br from-amber-500/20 to-amber-600/20 border-2 border-amber-500 scale-105'
                  : 'bg-white/5 border border-white/10 hover:border-amber-500/50'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <Star size={14} /> Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                <div className="text-4xl font-bold mb-2">
                  £{pkg.price}
                </div>
                <p className="text-gray-400 text-sm">{pkg.description}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className={`w-full py-3 rounded-full font-semibold transition-all ${
                  selectedPackage?.id === pkg.id
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {selectedPackage?.id === pkg.id ? 'Selected' : 'Select Package'}
              </button>
            </Reveal>
          )))
        }
        </div>

        {/* Personalised song upsell */}
        <MakeItPersonal added={formData.personalisedSong} onAdd={handleAddSong} />

        {/* Package Comparison Table */}
        <PackageComparison />

        {/* Order Form */}
        {selectedPackage && (
          <div ref={orderFormRef} className="scroll-mt-44">
            {/* Urgency banner */}
            <Reveal className="max-w-2xl mx-auto mb-4">
              <div data-testid="pricing-availability-banner" className="flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/15 to-amber-500/10 border border-amber-500/40 text-sm text-amber-100">
                <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                </span>
                <span>
                  {pricingBanner}{' '}
                  <Link to={REFUND_POLICY_LINK} data-testid="pricing-banner-refund-link" className="underline underline-offset-2 text-amber-300 hover:text-amber-200 whitespace-nowrap">
                    Cancellations &amp; refunds
                  </Link>
                </span>
              </div>
            </Reveal>
            <Reveal className="max-w-2xl mx-auto bg-white/5 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold mb-6">Complete Your Order</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      onBlur={() => setTouched({...touched, name: true})}
                      data-testid="form-name-input"
                      className={`${fieldClass('name')} pr-10`}
                      required
                    />
                    {showSuccess('name') && (
                      <CheckCircle2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400 pointer-events-none" />
                    )}
                    {showError('name') && (
                      <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" />
                    )}
                  </div>
                  {showError('name') && (
                    <p className="text-xs text-red-400 mt-1">Please enter your full name (at least 2 characters)</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      onBlur={() => setTouched({...touched, email: true})}
                      data-testid="form-email-input"
                      className={`${fieldClass('email')} pr-10`}
                      required
                    />
                    {showSuccess('email') && (
                      <CheckCircle2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400 pointer-events-none" />
                    )}
                    {showError('email') && (
                      <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" />
                    )}
                  </div>
                  {showError('email') && (
                    <p className="text-xs text-red-400 mt-1">Please enter a valid email address</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      onBlur={() => setTouched({...touched, phone: true})}
                      data-testid="form-phone-input"
                      className={`${fieldClass('phone')} pr-10`}
                    />
                    {showSuccess('phone') && (
                      <CheckCircle2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-400 pointer-events-none" />
                    )}
                    {showError('phone') && (
                      <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" />
                    )}
                  </div>
                  {showError('phone') && (
                    <p className="text-xs text-red-400 mt-1">Please enter a valid phone number</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type *</label>
                  <select
                    value={formData.slideshowType}
                    onChange={(e) => setFormData({...formData, slideshowType: e.target.value})}
                    onBlur={() => setTouched({...touched, slideshowType: true})}
                    data-testid="form-event-type"
                    className={`w-full px-4 py-3 bg-gray-800 text-white border rounded-lg focus:outline-none [color-scheme:dark] transition-colors ${
                      showError('slideshowType')
                        ? 'border-red-500 focus:border-red-400'
                        : showSuccess('slideshowType')
                        ? 'border-green-500/70 focus:border-green-400'
                        : 'border-white/20 focus:border-amber-500'
                    }`}
                    required
                  >
                    <option value="" className="bg-gray-800 text-white">Select type</option>
                    <option value="wedding" className="bg-gray-800 text-white">Wedding</option>
                    <option value="birthday" className="bg-gray-800 text-white">Birthday</option>
                    <option value="memorial" className="bg-gray-800 text-white">Memorial</option>
                    <option value="anniversary" className="bg-gray-800 text-white">Anniversary</option>
                    <option value="other" className="bg-gray-800 text-white">Other</option>
                  </select>
                  {showError('slideshowType') && (
                    <p className="text-xs text-red-400 mt-1">Please choose an event type</p>
                  )}
                </div>
              </div>

              {/* Add-ons */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Add-ons (Optional)</h3>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.rushDelivery}
                    onChange={(e) => setFormData({...formData, rushDelivery: e.target.checked})}
                    data-testid="addon-rush-checkbox"
                    className="w-5 h-5 accent-amber-500"
                  />
                  <span className="flex-1">Priority Delivery (24hrs)</span>
                  <span className="font-semibold">+£15</span>
                </label>

                <div className="flex items-center gap-4">
                  <label htmlFor="extra-revisions" className="flex-1">Additional Revisions (£5 each)</label>
                  <input
                    id="extra-revisions"
                    data-testid="addon-revisions-input"
                    type="number"
                    min="0"
                    max="5"
                    value={formData.extraRevisions}
                    onChange={(e) => setFormData({...formData, extraRevisions: Math.min(5, Math.max(0, parseInt(e.target.value) || 0))})}
                    className="w-20 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg focus:border-amber-500 focus:outline-none text-center"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4 hover:border-amber-500/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.personalisedSong}
                    onChange={(e) => { setFormData({...formData, personalisedSong: e.target.checked}); setSongErrors(false); }}
                    data-testid="addon-song-checkbox"
                    className="w-5 h-5 mt-0.5 accent-amber-500 flex-shrink-0"
                  />
                  <span className="flex-1">
                    <span className="block font-medium text-white">{SONG_ADDON.name}</span>
                    <span className="block text-sm text-gray-400 mt-1">{SONG_ADDON.description}</span>
                  </span>
                  <span className="font-semibold">+£{SONG_ADDON.price}</span>
                </label>

                {formData.personalisedSong && (
                  <SongDetailsFields
                    value={formData.songDetails}
                    onChange={(songDetails) => setFormData({...formData, songDetails})}
                    showErrors={songErrors}
                  />
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Upload Photos/Videos</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  disabled={uploadLater}
                  data-testid="order-file-input"
                  className="w-full px-4 py-3 bg-white/10 text-white border border-white/20 rounded-lg focus:border-amber-500 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-amber-500 file:text-black file:font-semibold hover:file:bg-amber-600"
                />
                {files.length > 0 && (
                  <p className="text-sm text-amber-400 mt-2">{files.length} file(s) selected</p>
                )}
                
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadLater}
                    data-testid="upload-later-checkbox"
                    onChange={(e) => {
                      setUploadLater(e.target.checked);
                      if (e.target.checked) setFiles([]);
                    }}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-300">I'll upload files later (we'll provide a QR code after payment for easy upload from your phone)</span>
                </label>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-2">Special Requests</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/10 text-white placeholder-gray-400 border border-white/20 rounded-lg focus:border-amber-500 focus:outline-none resize-none"
                  placeholder="Any special requests or instructions..."
                />
              </div>

              {/* Order Summary */}
              <OrderSummary packageName={selectedPackage.name} summary={summary} />

              {/* Delivery Information note */}
              <div
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                data-testid="delivery-info-note"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full border border-amber-500/40 bg-black flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400">
                      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-2 uppercase tracking-wider">
                      Digital & USB Delivery
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Your completed presentation will be available to download from your private Google Drive link for <span className="text-amber-300 font-medium">30 days</span> after delivery. Please download and save your presentation during this time.
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed mt-3">
                      <span className="text-amber-300 font-medium">Every Visual Creationz order includes a physical USB copy</span> of your finished presentation, giving you both a convenient digital version and a physical copy to keep.
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed mt-3">
                      Production times refer to the creation of your digital film. Physical USB delivery times may vary depending on postage.
                    </p>
                  </div>
                </div>
              </div>

              {/* USB Delivery Address notice */}
              <div
                className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-5"
                data-testid="usb-delivery-address-notice"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full border border-amber-500/40 bg-black flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm mb-2 uppercase tracking-wider">
                      USB Delivery Address
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Every Visual Creationz order includes a physical USB copy of your finished presentation. You will be asked to provide the address you'd like your USB delivered to at the next step of secure checkout.
                    </p>
                  </div>
                </div>
              </div>

              <RefundPolicy compact />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="pay-deposit-btn"
                className={`w-full px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-xl ${
                  isSubmitting
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-600 hover:to-amber-700 hover:shadow-amber-500/50 hover:scale-105 active:scale-95'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {files.length > 0 && !uploadLater ? 'Uploading Files...' : 'Processing...'}
                  </span>
                ) : (
                  `Continue to Secure Checkout - Pay £${calculateDeposit()} Deposit`
                )}
              </button>

              <p className="text-center text-sm text-gray-400">
                Secure payment via Stripe • Delivery address collected at checkout • Remaining balance due before final delivery
              </p>
            </form>
          </Reveal>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
